(() => {
  const HOVER_MS = 60;
  const FADE_OUT_MS = 180;
  const EXIT_MS = 200;
  const SUB_OUT_MS = 160;

  const prefersReduced = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const EdenPeak = (window.EdenPeakTheme = window.EdenPeakTheme || {});
  const cleanups = new Set();

  function on(el, type, handler, options) {
    if (!el) return;
    el.addEventListener(type, handler, options);
    cleanups.add(() => el.removeEventListener(type, handler, options));
  }

  function destroyHeaderBindings() {
    cleanups.forEach((fn) => fn());
    cleanups.clear();
  }

  /* ---------- Sticky header ---------- */
  function initStickyHeader(scope) {
    const header = scope.querySelector("[data-site-header]");
    if (!header) return;
    const onScroll = () =>
      header.classList.toggle("is-scrolled", window.scrollY > 20);
    on(window, "scroll", onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Mobile drawer ---------- */
  function initMobileDrawer(scope) {
    const drawer = document.querySelector("[data-mobile-drawer]");
    const backdrop = document.querySelector("[data-mobile-backdrop]");
    if (!drawer) return;

    const openMobile = () => {
      drawer.classList.add("is-open");
      backdrop?.classList.add("is-open");
      document.body.style.overflow = "hidden";
    };
    const closeMobile = () => {
      drawer.classList.remove("is-open");
      backdrop?.classList.remove("is-open");
      document.body.style.overflow = "";
    };

    EdenPeak.closeMobile = closeMobile;

    scope.querySelectorAll("[data-mobile-open]").forEach((el) => {
      on(el, "click", openMobile);
    });
    document.querySelectorAll("[data-mobile-close]").forEach((el) => {
      on(el, "click", closeMobile);
    });
    on(backdrop, "click", closeMobile);

    drawer.querySelectorAll("[data-accordion-mobile]").forEach((btn) => {
      on(btn, "click", () => {
        const panel = btn.parentElement.querySelector("[data-mobile-panel]");
        if (!panel) return;
        const open = panel.classList.toggle("is-open");
        btn.setAttribute("aria-expanded", open ? "true" : "false");
      });
    });
  }

  /* ---------- Mega menu N1 / N2 / N3 ---------- */
  function initSubmenu(panel) {
    if (!panel || panel.dataset.submenuReady === "1") return;
    panel.dataset.submenuReady = "1";

    const reduced = prefersReduced();
    const subBtns = [...panel.querySelectorAll("[data-sub-trigger]")];
    const details = [...panel.querySelectorAll("[data-sub-detail]")];
    if (!subBtns.length) return;

    let active = subBtns[0].dataset.subTrigger;
    let displayed = active;
    let timer = null;
    let swapTimer = null;

    const render = (id, animate) => {
      subBtns.forEach((b) => {
        const on = b.dataset.subTrigger === id;
        b.classList.toggle("is-active", on);
        b.setAttribute("aria-expanded", on ? "true" : "false");
      });
      details.forEach((d) => {
        const match = d.dataset.subDetail === id;
        d.hidden = !match;
        d.classList.remove("is-sub-out", "is-sub-in");
        if (match && animate && !reduced) d.classList.add("is-sub-in");
      });
      displayed = id;
      active = id;
    };

    const requestSub = (id, immediate = false) => {
      clearTimeout(timer);
      if (!id || id === active) return;
      const run = () => {
        clearTimeout(swapTimer);
        const current = panel.querySelector(`[data-sub-detail="${displayed}"]`);
        if (current && !reduced) {
          current.classList.add("is-sub-out");
          swapTimer = setTimeout(() => render(id, true), SUB_OUT_MS);
        } else {
          render(id, !reduced);
        }
      };
      timer = setTimeout(run, immediate ? 0 : 40);
    };

    subBtns.forEach((b) => {
      on(b, "mouseenter", () => requestSub(b.dataset.subTrigger));
      on(b, "focus", () => requestSub(b.dataset.subTrigger, true));
      on(b, "click", (e) => {
        e.preventDefault();
        requestSub(b.dataset.subTrigger, true);
      });
      on(b, "keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          requestSub(b.dataset.subTrigger, true);
        }
      });
    });

    render(active, false);
  }

  function initMegaMenu(scope) {
    const megaRoot = scope.querySelector("[data-mega-root]");
    if (!megaRoot) return;

    const shell = megaRoot.querySelector("[data-mega-shell]");
    if (!shell) return;

    const backdrop =
      scope.querySelector("[data-mega-backdrop]") ||
      document.querySelector("[data-mega-backdrop]");
    const panels = [...megaRoot.querySelectorAll("[data-mega-panel]")];
    const triggers = [...megaRoot.querySelectorAll("[data-mega-trigger]")];
    if (!triggers.length || !panels.length) return;

    let activeId = null;
    let displayedId = null;
    let hoverTimer = null;
    let leaveTimer = null;
    let swapTimers = [];
    const reduced = prefersReduced();
    const isDesktop = () => window.matchMedia("(min-width: 1024px)").matches;

    const clearSwap = () => {
      swapTimers.forEach(clearTimeout);
      swapTimers = [];
    };

    const showBackdrop = (deep = false) => {
      if (!backdrop || !isDesktop()) return;
      backdrop.classList.add("is-visible");
      backdrop.classList.toggle("is-deep", !!deep);
      backdrop.setAttribute("aria-hidden", "false");
    };

    const setBackdropDepth = (deep) => {
      if (!backdrop || !backdrop.classList.contains("is-visible")) return;
      backdrop.classList.toggle("is-deep", !!deep);
    };

    const hideBackdrop = () => {
      if (!backdrop) return;
      backdrop.classList.remove("is-visible", "is-deep");
      backdrop.setAttribute("aria-hidden", "true");
    };

    const setTriggerState = (id) => {
      triggers.forEach((t) => {
        const on = t.dataset.megaTrigger === id;
        t.classList.toggle("is-active", on);
        t.setAttribute("aria-expanded", on ? "true" : "false");
      });
      shell.setAttribute("aria-hidden", id ? "false" : "true");
    };

    const showPanel = (id, animate) => {
      panels.forEach((p) => {
        const match = p.dataset.megaPanel === id;
        p.hidden = !match;
        p.classList.remove("is-out", "is-in");
        if (match && animate && !reduced) p.classList.add("is-in");
      });
      displayedId = id;
      initSubmenu(megaRoot.querySelector(`[data-mega-panel="${id}"]`));
    };

    const openShell = (id) => {
      clearTimeout(leaveTimer);
      activeId = id;
      setTriggerState(id);

      if (!shell.classList.contains("is-open")) {
        shell.classList.remove("is-closing");
        shell.classList.add("is-open");
        showBackdrop(false);
        showPanel(id, !reduced);
        return;
      }

      if (displayedId === id) return;

      const current = megaRoot.querySelector(
        `[data-mega-panel="${displayedId}"]`
      );
      clearSwap();
      if (current && !reduced) {
        current.classList.add("is-out");
        swapTimers.push(
          setTimeout(() => {
            showPanel(id, true);
          }, FADE_OUT_MS)
        );
      } else {
        showPanel(id, !reduced);
      }
    };

    const closeShell = () => {
      activeId = null;
      setTriggerState(null);
      shell.classList.add("is-closing");
      shell.classList.remove("is-open");
      hideBackdrop();
      leaveTimer = setTimeout(() => {
        shell.classList.remove("is-closing");
        panels.forEach((p) => {
          p.hidden = true;
          p.classList.remove("is-out", "is-in");
        });
        displayedId = null;
      }, EXIT_MS);
    };

    EdenPeak.closeMega = closeShell;

    const request = (id, immediate = false) => {
      clearTimeout(hoverTimer);
      if (!id) {
        hoverTimer = setTimeout(closeShell, immediate ? 0 : HOVER_MS);
        return;
      }
      clearTimeout(leaveTimer);
      /* Dim as soon as N1 is hovered — do not wait for shell open. */
      showBackdrop(false);
      hoverTimer = setTimeout(() => openShell(id), immediate ? 0 : HOVER_MS);
    };

    triggers.forEach((t) => {
      const id = t.dataset.megaTrigger;
      on(t, "mouseenter", () => {
        setBackdropDepth(false);
        request(id);
      });
      on(t, "focus", () => {
        setBackdropDepth(false);
        request(id, true);
      });
      on(t, "click", (e) => {
        e.preventDefault();
        if (activeId === id) request(null, true);
        else request(id, true);
      });
      on(t, "keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (activeId === id) request(null, true);
          else request(id, true);
        }
      });
    });

    megaRoot.querySelectorAll("[data-mega-plain]").forEach((el) => {
      on(el, "mouseenter", () => request(null));
      on(el, "focus", () => request(null, true));
    });

    on(megaRoot, "mouseleave", () => request(null));
    on(shell, "mouseenter", () => {
      clearTimeout(hoverTimer);
      clearTimeout(leaveTimer);
      showBackdrop(true);
    });
    on(shell, "mouseleave", () => {
      setBackdropDepth(false);
    });

    on(backdrop, "click", () => request(null, true));

    on(document, "keydown", (e) => {
      if (e.key !== "Escape") return;
      closeShell();
      EdenPeak.closeMobile?.();
      EdenPeak.closeSearch?.();
      EdenPeak.closeCart?.();
    });

    on(document, "mousedown", (e) => {
      if (e.target.closest("[data-mega-backdrop]")) return;
      if (!e.target.closest("[data-header-root]")) request(null, true);
    });
  }

  /* ---------- Search (once, global) ---------- */
  function initSearch() {
    if (EdenPeak.searchReady) return;
    EdenPeak.searchReady = true;

    const searchOverlay = document.querySelector("[data-search-overlay]");
    const searchInput = searchOverlay?.querySelector("[data-search-input]");
    const searchResults = searchOverlay?.querySelector("[data-search-results]");
    let searchTimer;

    const openSearch = () => {
      searchOverlay?.classList.add("is-open");
      document.body.style.overflow = "hidden";
      setTimeout(() => searchInput?.focus(), 40);
    };
    const closeSearch = () => {
      searchOverlay?.classList.remove("is-open");
      document.body.style.overflow = "";
      if (searchInput) searchInput.value = "";
      if (searchResults) searchResults.innerHTML = "";
    };
    EdenPeak.closeSearch = closeSearch;

    document.addEventListener("click", (e) => {
      if (e.target.closest("[data-search-open]")) {
        e.preventDefault();
        openSearch();
      }
      if (e.target.closest("[data-search-close]")) closeSearch();
      if (e.target === searchOverlay) closeSearch();
    });

    searchInput?.addEventListener("input", () => {
      clearTimeout(searchTimer);
      const q = searchInput.value.trim();
      if (!q) {
        searchResults.innerHTML = "";
        return;
      }
      searchTimer = setTimeout(async () => {
        try {
          const root = window.Shopify?.routes?.root || "/";
          const res = await fetch(
            `${root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`
          );
          const data = await res.json();
          const products = data?.resources?.results?.products || [];
          if (!products.length) {
            searchResults.innerHTML = `<p class="rte">${searchResults.dataset.empty || ""}</p>`;
            return;
          }
          searchResults.innerHTML = products
            .map(
              (p) => `
            <a class="search-overlay__result" href="${p.url}">
              ${
                p.image
                  ? `<img src="${p.image}" alt="" width="48" height="64" loading="lazy">`
                  : `<span style="width:3rem;height:4rem;background:var(--color-surface)"></span>`
              }
              <span style="flex:1;min-width:0">
                <span class="search-overlay__result-title">${p.title}</span>
                <span class="search-overlay__result-meta">${p.product_type || ""}</span>
              </span>
              <span class="product-card__price">${p.price}</span>
            </a>`
            )
            .join("");
        } catch (_) {
          searchResults.innerHTML = "";
        }
      }, 180);
    });

    searchInput?.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && searchInput.value.trim()) {
        const root = window.Shopify?.routes?.root || "/";
        window.location.href = `${root}search?q=${encodeURIComponent(searchInput.value.trim())}`;
      }
    });
  }

  /* ---------- Cart (once, global) ---------- */
  function initCart() {
    if (EdenPeak.cartReady) return;
    EdenPeak.cartReady = true;

    const cartDrawer = document.querySelector("[data-cart-drawer]");
    const cartBackdrop = document.querySelector("[data-cart-backdrop]");
    const cartBody = cartDrawer?.querySelector("[data-cart-body]");
    const cartSubtotal = cartDrawer?.querySelector("[data-cart-subtotal]");

    const formatMoney = (cents) => {
      try {
        return window.Shopify?.formatMoney
          ? Shopify.formatMoney(cents, window.themeMoneyFormat)
          : (cents / 100).toFixed(2) + " €";
      } catch (_) {
        return (cents / 100).toFixed(2) + " €";
      }
    };

    const openCart = () => {
      cartDrawer?.classList.add("is-open");
      cartBackdrop?.classList.add("is-open");
      document.body.style.overflow = "hidden";
    };
    const closeCart = () => {
      cartDrawer?.classList.remove("is-open");
      cartBackdrop?.classList.remove("is-open");
      document.body.style.overflow = "";
    };
    EdenPeak.closeCart = closeCart;

    async function refreshCart() {
      const root = window.Shopify?.routes?.root || "/";
      const res = await fetch(root + "cart.js");
      const cart = await res.json();
      document.querySelectorAll("[data-cart-count]").forEach((el) => {
        el.textContent = String(cart.item_count || 0);
        el.hidden = !cart.item_count;
      });
      if (cartSubtotal) cartSubtotal.textContent = formatMoney(cart.total_price);
      if (!cartBody) return cart;
      if (!cart.items?.length) {
        cartBody.innerHTML = `<p class="empty-state">${cartBody.dataset.empty || ""}</p>`;
        return cart;
      }
      cartBody.innerHTML = cart.items
        .map(
          (item) => `
        <div class="cart-line" data-key="${item.key}">
          <img src="${item.image || ""}" alt="" width="72" height="88" loading="lazy">
          <div>
            <div class="cart-line__title">${item.product_title}</div>
            <div class="search-overlay__result-meta">${item.variant_title || ""}</div>
            <div class="cart-line__price">${formatMoney(item.final_line_price)}</div>
            <div class="cart-line__qty">
              <button type="button" data-qty-change="${item.key}" data-qty="${item.quantity - 1}" aria-label="-">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-qty-change="${item.key}" data-qty="${item.quantity + 1}" aria-label="+">+</button>
              <button type="button" data-qty-change="${item.key}" data-qty="0" style="margin-left:auto;border:0;background:none;text-decoration:underline;width:auto;font-weight:500">${cartBody.dataset.remove || "×"}</button>
            </div>
          </div>
        </div>`
        )
        .join("");
      return cart;
    }

    document.addEventListener("click", (e) => {
      const openBtn = e.target.closest("[data-cart-open]");
      if (openBtn) {
        e.preventDefault();
        refreshCart().then(openCart);
      }
      if (e.target.closest("[data-cart-close]")) closeCart();
      if (e.target === cartBackdrop) closeCart();
    });

    cartBody?.addEventListener("click", async (e) => {
      const btn = e.target.closest("[data-qty-change]");
      if (!btn) return;
      const key = btn.getAttribute("data-qty-change");
      const quantity = Number(btn.getAttribute("data-qty"));
      const root = window.Shopify?.routes?.root || "/";
      await fetch(root + "cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity }),
      });
      await refreshCart();
    });

    document.addEventListener("submit", async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches("form[action*='/cart/add'][data-ajax-cart]")) return;
      e.preventDefault();
      const root = window.Shopify?.routes?.root || "/";
      await fetch(root + "cart/add.js", { method: "POST", body: new FormData(form) });
      await refreshCart();
      openCart();
    });

    refreshCart().catch(() => {});
  }

  /* ---------- Accordion / gallery ---------- */
  function initAccordions(scope) {
    scope.querySelectorAll("[data-accordion-trigger]").forEach((btn) => {
      if (btn.dataset.bound === "1") return;
      btn.dataset.bound = "1";
      btn.addEventListener("click", () => {
        const panel = btn.parentElement.querySelector("[data-accordion-panel]");
        if (!panel) return;
        const open = panel.classList.toggle("is-open");
        btn.setAttribute("aria-expanded", open ? "true" : "false");
      });
    });
  }

  function initGalleries(scope) {
    scope.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
      if (gallery.dataset.bound === "1") return;
      gallery.dataset.bound = "1";
      const main = gallery.querySelector("[data-gallery-main]");
      gallery.querySelectorAll("[data-gallery-thumb]").forEach((thumb) => {
        thumb.addEventListener("click", () => {
          const src = thumb.getAttribute("data-src");
          if (main && src) {
            main.src = src;
            gallery
              .querySelectorAll("[data-gallery-thumb]")
              .forEach((t) => t.classList.remove("is-active"));
            thumb.classList.add("is-active");
          }
        });
      });
    });
  }

  /* ---------- Public init ---------- */
  EdenPeak.initHeader = function initHeader(scope) {
    const root = scope || document;
    destroyHeaderBindings();
    initStickyHeader(root);
    initMobileDrawer(root);
    initMegaMenu(root);
  };

  function initScrollTop() {
    const btn = document.querySelector("[data-scroll-top]");
    if (!btn || EdenPeak.scrollTopReady) return;
    EdenPeak.scrollTopReady = true;

    const sync = () => {
      const show = window.scrollY > 600;
      btn.classList.toggle("is-visible", show);
      if (show) btn.removeAttribute("hidden");
      else btn.setAttribute("hidden", "");
    };

    /* Global once — not tied to header binding cleanups. */
    window.addEventListener("scroll", sync, { passive: true });
    btn.addEventListener("click", () => {
      const reduced = prefersReduced();
      window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
    });
    sync();
  }

  function initHeroMotion(scope) {
    const hero = (scope || document).querySelector("[data-hero]");
    if (!hero || hero.dataset.heroBound === "1") return;
    hero.dataset.heroBound = "1";

    const motion = hero.getAttribute("data-hero-motion") === "true";
    if (!motion) return;

    const reduced = prefersReduced();
    const mobileVideo = hero.getAttribute("data-hero-mobile-video") === "true";
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const slides = [...hero.querySelectorAll("[data-hero-slide]")];
    if (!slides.length) return;

    const allowVideo = !reduced && (mobileVideo || !isMobile);
    if (!allowVideo || reduced) {
      hero.classList.add("hero--no-video");
    }

    const hasAnyVideo =
      allowVideo &&
      slides.some((s) => s.querySelector("[data-hero-video] source[src]"));
    if (!hasAnyVideo) hero.classList.add("hero--no-video");

    const interval = Number(hero.getAttribute("data-hero-interval") || 8000);
    const pauseHover = hero.getAttribute("data-hero-pause-hover") === "true";
    const dots = [...hero.querySelectorAll("[data-hero-dots] .hero__dot")];
    let index = 0;
    let timer = null;
    let paused = false;

    const playSlide = (slide) => {
      if (!hasAnyVideo) return;
      const video = slide.querySelector("[data-hero-video]");
      if (!video) return;
      const tryPlay = () => {
        video.muted = true;
        const p = video.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      };
      if (video.readyState >= 2) {
        video.classList.add("is-ready");
        tryPlay();
      } else {
        video.load();
        video.addEventListener(
          "loadeddata",
          () => {
            video.classList.add("is-ready");
            tryPlay();
          },
          { once: true }
        );
        video.addEventListener(
          "error",
          () => {
            hero.classList.add("hero--no-video");
          },
          { once: true }
        );
      }
    };

    const show = (next) => {
      slides.forEach((s, i) => {
        const on = i === next;
        s.classList.toggle("is-active", on);
        const v = s.querySelector("[data-hero-video]");
        if (!on && v) {
          v.pause();
          try {
            v.currentTime = 0;
          } catch (_) {}
        }
      });
      dots.forEach((d, i) => d.classList.toggle("is-active", i === next));
      playSlide(slides[next]);
      index = next;
    };

    const tick = () => {
      if (paused || slides.length < 2 || reduced) return;
      show((index + 1) % slides.length);
    };

    const arm = () => {
      clearInterval(timer);
      if (slides.length > 1 && !reduced) timer = setInterval(tick, interval);
    };

    show(0);
    arm();

    if (pauseHover && !isMobile) {
      hero.addEventListener("mouseenter", () => {
        paused = true;
      });
      hero.addEventListener("mouseleave", () => {
        paused = false;
      });
    }

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        slides.forEach((s) => s.querySelector("[data-hero-video]")?.pause());
      } else if (hasAnyVideo) {
        playSlide(slides[index]);
      }
    });
  }

  EdenPeak.initPage = function initPage(scope) {
    const root = scope || document;
    initAccordions(root);
    initGalleries(root);
    initScrollTop();
    initHeroMotion(root);
  };

  function boot(scope) {
    EdenPeak.initHeader(scope || document);
    EdenPeak.initPage(scope || document);
    initSearch();
    initCart();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    boot(event.target);
  });

  document.addEventListener("shopify:section:unload", () => {
    destroyHeaderBindings();
  });

  document.addEventListener("shopify:section:select", (event) => {
    if (event.target.querySelector?.("[data-mega-root]")) {
      EdenPeak.initHeader(event.target);
    }
  });
})();
